import React, { useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { act01 as t1, DOWNLOAD_URL } from '../data/story';
import { useAct, gsap, DEBUG, prefersReduced } from '../hooks/useScrollStory';

/* ══════════════════════════════════════════════════════
   HERO — kaydırmayla sarılan video
   El fidanı toprağa yerleştiriyor, kamera toprak çizgisinin
   altına iniyor, kök sistemi açılıyor. Kaydırma yönü ile
   hareket yönü aynı: aşağı kaydırdıkça aşağı iniyorsun.

   • Video Blob olarak indiriliyor. Bazı sunucular parçalı
     indirmeyi desteklemiyor, o zaman her arama sıfıra düşer
     ve sarma hiç çalışmaz. Blob her yerde çalışır.
   • Aramalar kapılı: öncekiler bitmeden yenisi yazılmıyor.
   • Yumuşatmayı GSAP scrub yapıyor, ayrı bir döngü yok.
   • Video hiç gelmezse poster ve metinlerle sayfa eksiksiz.
   ══════════════════════════════════════════════════════ */

const SRC = '/assets/video/hero.mp4';
const POSTER = '/assets/video/hero-poster.jpg';
const UNIT_VH = 120;
const UNITS = 3.0;

export default function HeroVideo() {
  const vidRef = useRef(null);

  const ref = useAct((root) => {
    const q = gsap.utils.selector(root);
    const video = vidRef.current;
    const ring = q('.v-ring')[0];

    /* Kare ızgarasına oturan kapılı arama — ayrıntılı gerekçe
       SatelliteToFields.jsx içindeki makeDriver yorumunda. Özet:
       aynı kareye tekrar seek atma, aramaları üst üste bindirme,
       zaman aşımında yalnız kilidi aç (yeniden arama başlatma). */
    const FPS = 12;
    let seekBusy = false, pending = null, seekTimer = null, lastFrame = -1;
    const clearSeekTimer = () => { if (seekTimer) { clearTimeout(seekTimer); seekTimer = null; } };
    function runSeek(t) {
      seekBusy = true;
      clearSeekTimer();
      seekTimer = setTimeout(() => { seekTimer = null; seekBusy = false; }, 250);
      try { video.currentTime = t; } catch (e) { clearSeekTimer(); seekBusy = false; }
    }
    function requestSeek(t) {
      const d = video.duration;
      if (!d) return;
      const frame = Math.round(t * FPS);
      if (frame === lastFrame) return;
      lastFrame = frame;
      const target = Math.min(d - 0.001, frame / FPS);
      if (seekBusy) { pending = target; return; }
      runSeek(target);
    }
    video.addEventListener('seeked', () => {
      clearSeekTimer();
      seekBusy = false;
      if (pending !== null) { const t = pending; pending = null; runSeek(t); }
    });
    video.addEventListener('error', () => { pending = null; clearSeekTimer(); seekBusy = false; });

    let ready = false;
    const load = async () => {
      try {
        const res = await fetch(SRC);
        if (!res.ok || !res.body) throw new Error('video yok');
        const total = Number(res.headers.get('Content-Length')) || 7400000;
        const reader = res.body.getReader();
        const parts = [];
        let got = 0, last = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          parts.push(value);
          got += value.length;
          const now = performance.now();
          if (now - last > 100) {
            last = now;
            if (ring) ring.style.setProperty('--ld', Math.round(126 * (1 - Math.min(1, got / total))));
          }
        }
        if (ring) { ring.style.setProperty('--ld', 0); ring.classList.remove('on'); }
        video.src = URL.createObjectURL(new Blob(parts, { type: 'video/mp4' }));
        video.load();
        video.addEventListener('canplay', () => {
          ready = true;
          root.classList.add('v-ready');
          const r = root.getBoundingClientRect();
          const span = root.offsetHeight - window.innerHeight;
          const p = span <= 0 ? 0 : Math.min(1, Math.max(0, -r.top / span));
          requestSeek(p * video.duration);
        }, { once: true });
      } catch (e) {
        if (ring) ring.classList.remove('on');
      }
    };

    if (prefersReduced()) {
      gsap.set(q('.band'), { opacity: 1, y: 0 });
      return;
    }

    if (ring) ring.classList.add('on');
    load();

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: root,
        start: 'top top',
        end: 'bottom bottom',
        /* sıkı scrub: bırakınca hemen dursun, kendi kendine akmasın */
        scrub: 0.2,
        markers: DEBUG,
        onUpdate: (self) => {
          if (ready && video.duration) requestSeek(self.progress * video.duration);
        },
      },
    });

    /* Bantlar aynı noktada duruyor, pencereleri çakışmamalı.
       Giriş 0,40 ve çıkış 0,35 birim sürüyor; her bant tamamen
       kaybolduktan sonra bir sonraki giriyor. */
    const band = (sel, inAt, outAt) => {
      const el = q(sel)[0];
      if (!el) return;
      tl.fromTo(el, { opacity: 0, y: 26 }, { opacity: 1, y: 0, ease: 'power3.out', duration: 0.4 }, inAt);
      if (outAt !== null) tl.to(el, { opacity: 0, y: -18, ease: 'power2.in', duration: 0.35 }, outAt);
    };

    /* ilk bant açılışta oturmuş duruyor, kaydırınca çekiliyor */
    gsap.set(q('.b-hero'), { opacity: 1, y: 0 });
    gsap.set([q('.b-deep'), q('.b-root')], { opacity: 0 });
    tl.to(q('.b-hero'), { opacity: 0, y: -60, ease: 'power2.in', duration: 0.6 }, 0.35);
    tl.to(q('.cue'), { opacity: 0, duration: 0.35 }, 0.12);

    band('.b-deep', 1.30, 2.10);   // 1,30 → 2,45
    band('.b-root', 2.60, null);   // 2,60 → sonuna kadar

    tl.to(q('.note'), { opacity: 0, duration: 0.4 }, 1.2);
  }, []);

  return (
    <section
      ref={ref}
      id="top"
      className="relative"
      style={{ height: `${UNITS * UNIT_VH}vh`, background: '#050605' }}
    >
      <div className="sticky top-0 h-screen overflow-hidden" style={{ background: '#050605' }}>
        <video
          ref={vidRef}
          className="absolute inset-0 w-full h-full object-cover"
          poster={POSTER}
          preload="none"
          muted
          playsInline
          aria-hidden="true"
          tabIndex={-1}
        />

        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              'radial-gradient(ellipse 120% 90% at 50% 42%, rgba(5,6,5,0) 30%, rgba(5,6,5,.62) 100%)',
          }}
        />

        <svg
          className="v-ring absolute right-[max(24px,4vw)] top-[max(24px,4vh)] w-9 h-9 opacity-0 transition-opacity duration-500"
          viewBox="0 0 48 48"
          aria-hidden="true"
          style={{ transform: 'rotate(-90deg)', color: 'rgba(200,255,34,.85)' }}
        >
          <circle cx="24" cy="24" r="20" fill="none" stroke="currentColor" strokeWidth="2.5"
            strokeDasharray="126" style={{ strokeDashoffset: 'var(--ld,126)' }} />
        </svg>

        {/* açılış */}
        <div className="b-hero band absolute left-[max(24px,5vw)] right-[max(24px,5vw)] top-[16vh] max-w-[min(820px,92vw)]">
          <h1
            className="font-display font-bold text-white leading-[1.02] tracking-[-0.03em] clip-safe"
            style={{ fontSize: 'clamp(32px,7.2vw,104px)', textShadow: '0 2px 30px rgba(0,0,0,.55)' }}
          >
            {t1.title.map((l) => (
              <span key={l} className="block">{l}</span>
            ))}
          </h1>
          {t1.subtitle && (
            <p className="mt-6 text-[16px] md:text-[17px] leading-relaxed text-white/80 max-w-[54ch]"
              style={{ textShadow: '0 1px 14px rgba(0,0,0,.6)' }}>
              {t1.subtitle}
            </p>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href={DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-medium text-[#0A140A] border border-lime/40"
              style={{ background: '#C8FF22', boxShadow: '0 8px 30px rgba(200,255,34,.28)' }}
            >
              {t1.cta}
              <span aria-hidden="true">↓</span>
            </a>
            <a
              href="#ozellikler"
              className="inline-flex items-center gap-1.5 text-[15px] text-white/85 hover:text-white transition-colors duration-300"
            >
              {t1.cta2}
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>

        {/* kamera toprağın altına inerken */}
        <p
          className="b-deep band absolute left-1/2 top-[48%] -translate-x-1/2 text-center font-display font-semibold text-white px-6"
          style={{ fontSize: 'clamp(22px,3vw,44px)', textShadow: '0 2px 26px rgba(0,0,0,.75)' }}
        >
          Toprağın altını görmeden<br />karar veriyorsun.
        </p>

        {/* kök açığa çıkınca */}
        <div className="b-root band absolute left-[max(24px,5vw)] right-[max(24px,5vw)] top-[30vh] max-w-[min(620px,86vw)]">
          <h2
            className="font-display font-bold text-white leading-[1.05] tracking-[-0.03em] clip-safe"
            style={{ fontSize: 'clamp(28px,4vw,58px)', textShadow: '0 2px 28px rgba(0,0,0,.7)' }}
          >
            Agren AI kökü de görür.
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-white/78 max-w-[42ch]">
            {t1.deep ? 'Uydudan tarlaya, tarladan tek bir yaprağa. Tarlanın her karışını senin göremediğin yerden okur.' : ''}
          </p>
        </div>

        {/* alt satır ve kaydırma işareti */}
        <div className="absolute left-0 right-0 bottom-0 flex items-end justify-between gap-10 px-[max(24px,5vw)] pb-[max(26px,4vh)] pointer-events-none">
          <p className="note max-w-[52ch] text-[13.5px] leading-[1.45] text-white/85"
            style={{ textShadow: '0 1px 12px rgba(0,0,0,.75)' }}>
            {t1.note}
          </p>
          <div className="cue flex items-center gap-2.5 shrink-0 font-mono text-[11px] tracking-[.12em] uppercase text-white/85"
            style={{ textShadow: '0 1px 12px rgba(0,0,0,.75)' }}>
            <ChevronDown className="w-3.5 h-3.5 cue-arrow" aria-hidden="true" />
            <span>{t1.cue}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
